/**
 * Building blocks of the 375 px lists of Passes and Activity (SAM-944, canvas Phone* mocks): the title row with
 * «New pass», the one-dropdown switch, the search box, the filter capsules that slide with a fade on the right,
 * and the row card. Tailwind + tokens (styling freeze, SAM-175); the app has no Tailwind preflight, so borders,
 * backgrounds and the font are explicit.
 */
import React from 'react';
import { ChevronDown, Plus, Search } from 'lucide-react';
import { TONE_CLASS } from '../../pages/BusinessPage/ActivityControls';
import type { Tone } from '../../pages/BusinessPage/activity';
import { initials } from '../../pages/BusinessPage/activityViews';
import { useT } from '../../i18n/admin';

/** Page title with the primary action at its right (44 px target). */
export const PhoneTitleRow: React.FC<{ title: string; onNew?: () => void; newLabel?: string; summary?: string }> = ({ title, onNew, newLabel, summary }) => {
  const { t } = useT();
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <h1 className="m-0 text-2xl font-bold text-[var(--ink-900)]">{title}</h1>
        {summary && <div className="mt-0.5 text-[13px] text-[var(--ink-500)]">{summary}</div>}
      </div>
      {onNew && (
        <button
          type="button"
          onClick={onNew}
          className="inline-flex h-11 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[10px] border-0 bg-[var(--tenant-primary,#005488)] px-4 font-[inherit] text-sm font-semibold text-white"
        >
          <Plus size={16} aria-hidden />
          {newLabel ?? t('phone.newPass')}
        </button>
      )}
    </div>
  );
};

/** The page of a phone list: scrolls on its own, 16 px gutters (SAM-1308). */
export const PhonePage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-full min-h-0 min-w-0 flex-col gap-3 overflow-y-auto bg-[var(--surface-2,#f5f5f5)] px-4 pb-8 pt-4">{children}</div>
);

export interface PhoneOption { id: string; label: string }
export interface PhoneOptionGroup { label?: string; options: PhoneOption[] }

/**
 * A closed dropdown: the chosen option is written in the box and the native select sits on top of it (invisible),
 * so a phone opens its own picker and the control stays accessible.
 */
export const PhoneSelect: React.FC<{
  ariaLabel: string;
  value: string;
  display: string;
  groups: PhoneOptionGroup[];
  onChange: (id: string) => void;
}> = ({ ariaLabel, value, display, groups, onChange }) => (
  <label className="relative flex h-12 shrink-0 items-center justify-between rounded-[10px] border border-solid border-[var(--line)] bg-white px-3.5 text-base font-semibold text-[var(--ink-900)]">
    <span className="truncate">{display}</span>
    <ChevronDown size={16} aria-hidden className="shrink-0 text-[var(--ink-500)]" />
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
    >
      {groups.map((g, i) => {
        const opts = g.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>);
        return g.label ? <optgroup key={g.label} label={g.label}>{opts}</optgroup> : <React.Fragment key={i}>{opts}</React.Fragment>;
      })}
    </select>
  </label>
);

export const PhoneSearch: React.FC<{ placeholder: string; value: string; onChange: (v: string) => void }> = ({ placeholder, value, onChange }) => (
  <label className="flex h-11 shrink-0 items-center gap-2 rounded-lg border border-solid border-[var(--line)] bg-white px-3 text-[var(--ink-500)]">
    <Search size={16} aria-hidden className="shrink-0" />
    <input
      type="search"
      aria-label={placeholder}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-w-0 flex-1 border-0 bg-transparent p-0 font-[inherit] text-[15px] text-[var(--ink-900)] outline-none"
    />
  </label>
);

export interface PhoneCapsule { id: string; label: string; on: boolean }

/** One row of filter capsules that scrolls sideways; a fade on the right edge says there is more. */
export const PhoneCapsules: React.FC<{ ariaLabel: string; chips: PhoneCapsule[]; onPick: (id: string) => void }> = ({ ariaLabel, chips, onPick }) => (
  <div className="relative -mx-4 shrink-0 overflow-hidden">
    <div role="group" aria-label={ariaLabel} className="flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          aria-pressed={c.on}
          onClick={() => onPick(c.id)}
          className={`flex h-9 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full border border-solid px-3.5 font-[inherit] text-[13px] font-semibold ${
            c.on
              ? 'border-[var(--tenant-primary,#005488)] bg-[var(--tenant-primary,#005488)] text-white'
              : 'border-[var(--line)] bg-white text-[var(--ink-700)]'
          }`}
        >
          {c.label}
        </button>
      ))}
      <span aria-hidden className="w-6 shrink-0" />
    </div>
    <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-r from-transparent to-[var(--surface-2,#f5f5f5)]" />
  </div>
);

export interface PhoneRow {
  key: string;
  /** Top left: the time, or the day. Blue when it is today. */
  when: string;
  today?: boolean;
  status?: { label: string; tone: Tone } | null;
  name: string;
  sub?: string;
  /** Last line, small. */
  extra?: string;
  /** Replaces the initials circle (a logo). */
  avatar?: React.ReactNode;
  open?: () => void;
}

export const PhoneRowCard: React.FC<{ row: PhoneRow }> = ({ row }) => {
  const body = (
    <>
      <span className="flex items-center justify-between gap-2">
        <span className={`text-[13px] font-bold ${row.today ? 'text-[var(--tenant-primary,#005488)]' : 'text-[var(--ink-900)]'}`}>{row.when}</span>
        {row.status && (
          <span className={`whitespace-nowrap rounded-full px-2.5 py-[3px] text-xs font-semibold ${TONE_CLASS[row.status.tone]}`}>{row.status.label}</span>
        )}
      </span>
      <span className="mt-0.5 flex items-center gap-2.5">
        {row.avatar ?? (
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--tenant-primary,#005488)_15%,white)] text-xs font-bold text-[color-mix(in_srgb,var(--tenant-primary,#005488)_75%,black)]"
          >
            {initials(row.name)}
          </span>
        )}
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-semibold text-[var(--ink-900)]">{row.name}</span>
          {row.sub && <span className="block truncate text-[13px] text-[var(--ink-500)]">{row.sub}</span>}
        </span>
      </span>
      {row.extra && <span className="text-xs text-[var(--ink-500)]">{row.extra}</span>}
    </>
  );
  const cls = 'flex w-full flex-col gap-1.5 rounded-xl border border-solid border-[var(--line)] bg-white px-4 py-3.5 text-left font-[inherit]';
  return row.open
    ? <button type="button" onClick={row.open} className={`${cls} cursor-pointer`}>{body}</button>
    : <div className={cls}>{body}</div>;
};

export const PhoneRowList: React.FC<{ rows: PhoneRow[] }> = ({ rows }) => (
  <ul className="m-0 flex list-none flex-col gap-3 p-0">
    {rows.map((r) => <li key={r.key}><PhoneRowCard row={r} /></li>)}
  </ul>
);

export interface PhoneItem {
  key: string;
  title: string;
  /** Second line, one line (ellipsis). */
  sub?: string;
  /** Third line, small. */
  extra?: string;
  /** The picture at the left (a logo or initials); a neutral square when absent. */
  avatar?: React.ReactNode;
  status?: { label: string; tone: Tone } | null;
  open?: () => void;
}

/** A square for a logo: shown whole (contain), never cropped. */
export const PhoneLogo: React.FC<{ children?: React.ReactNode; fallback: React.ReactNode }> = ({ children, fallback }) => (
  <span aria-hidden className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-solid border-[var(--line)] bg-white [&_img]:h-full [&_img]:w-full [&_img]:object-contain">
    {children ?? fallback}
  </span>
);

export const PhoneItemCard: React.FC<{ item: PhoneItem }> = ({ item }) => {
  const body = (
    <>
      {item.avatar}
      <span className="min-w-0 grow">
        <span className="block truncate text-[15px] font-semibold text-[var(--ink-900)]">{item.title}</span>
        {item.sub && <span className="block truncate text-[13px] text-[var(--ink-500)]">{item.sub}</span>}
        {item.extra && <span className="block text-xs text-[var(--ink-500)]">{item.extra}</span>}
      </span>
      {item.status && (
        <span className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-[3px] text-xs font-semibold ${TONE_CLASS[item.status.tone]}`}>{item.status.label}</span>
      )}
    </>
  );
  const cls = 'flex w-full items-center gap-3 rounded-xl border border-solid border-[var(--line)] bg-white px-3.5 py-3 text-left font-[inherit]';
  return item.open
    ? <button type="button" onClick={item.open} className={`${cls} cursor-pointer`}>{body}</button>
    : <div className={cls}>{body}</div>;
};

export const PhoneItemList: React.FC<{ items: PhoneItem[] }> = ({ items }) => (
  <ul className="m-0 flex list-none flex-col gap-2 p-0">
    {items.map((i) => <li key={i.key}><PhoneItemCard item={i} /></li>)}
  </ul>
);
